/**
 * Gateway Remote Configuration Service
 *
 * Fetches gateway configuration from GCS on app startup.
 * Falls back to environment variables if remote fetch fails.
 */

import * as Sentry from '@sentry/react';

export interface GatewayConfig {
	enabled: boolean;
	primaryGatewayUrl: string;
	fallbackGatewayUrl: string;
	circuitBreaker: {
		consecutiveFailures: number;
		halfOpenAfterMs: number;
		errorCodes: number[];
	};
}

const CONFIG_FETCH_TIMEOUT = 5000;

/**
 * Default config from environment variables (fallback)
 */
const getDefaultConfig = (): GatewayConfig => ({
	enabled: true,
	primaryGatewayUrl: import.meta.env.REACT_APP_API_GATEWAY_URL || '',
	fallbackGatewayUrl: import.meta.env.REACT_APP_API_GATEWAY_FALLBACK_URL || '',
	circuitBreaker: {
		consecutiveFailures: 3,
		halfOpenAfterMs: 30_000, // 30 seconds
		errorCodes: [502, 503, 504, 520, 521, 522, 523, 524],
	},
});

/** In-memory cache for immediate access */
let configCache: GatewayConfig | null = null;

/** Singleton promise to prevent duplicate fetches */
let initializationPromise: Promise<GatewayConfig> | null = null;

/**
 * Validate that a remote config response matches the expected GatewayConfig shape
 */
function isValidGatewayConfig(c: unknown): c is GatewayConfig {
	if (!c || typeof c !== 'object') return false;
	const cfg = c as GatewayConfig;
	return (
		typeof cfg.enabled === 'boolean' &&
		typeof cfg.primaryGatewayUrl === 'string' &&
		typeof cfg.fallbackGatewayUrl === 'string' &&
		Array.isArray(cfg.circuitBreaker?.errorCodes) &&
		cfg.circuitBreaker.errorCodes.length > 0 &&
		typeof cfg.circuitBreaker.consecutiveFailures === 'number' &&
		typeof cfg.circuitBreaker.halfOpenAfterMs === 'number'
	);
}

/**
 * Fetch gateway config from GCS
 */
async function fetchRemoteConfig(): Promise<GatewayConfig> {
	const configUrl = import.meta.env.REACT_APP_API_GATEWAY_CONFIG_URL || '';

	if (!configUrl) {
		return getDefaultConfig();
	}

	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), CONFIG_FETCH_TIMEOUT);

	try {
		const response = await fetch(`${configUrl}?t=${Date.now()}`, {
			method: 'GET',
			headers: { Accept: 'application/json' },
			signal: controller.signal,
		});

		if (!response.ok) {
			throw new Error(`Config fetch failed: ${response.status}`);
		}

		const result = await response.json();

		if (result.stat_code === 'APP:SUCCESS' && isValidGatewayConfig(result.data)) {
			return result.data;
		}

		throw new Error('Invalid config response format');
	} finally {
		clearTimeout(timeoutId);
	}
}

/**
 * Initialize gateway config on app startup.
 * Uses singleton promise to prevent duplicate fetches.
 * Falls back to environment variables if remote fetch fails.
 */
export async function initGatewayConfig(): Promise<GatewayConfig> {
	if (configCache) {
		return configCache;
	}

	if (initializationPromise) {
		return initializationPromise;
	}

	initializationPromise = (async () => {
		try {
			const remoteConfig = await fetchRemoteConfig();
			configCache = remoteConfig;

			Sentry.addBreadcrumb({
				category: 'gateway',
				message: 'Remote gateway config loaded',
				level: 'info' as Sentry.Severity,
				data: {
					primaryUrl: remoteConfig.primaryGatewayUrl,
					fallbackUrl: remoteConfig.fallbackGatewayUrl,
				},
			});

			return remoteConfig;
		} catch (error) {
			Sentry.captureException(error, {
				tags: { component: 'gateway', phase: 'config-fetch' },
			});
			Sentry.addBreadcrumb({
				category: 'gateway',
				message: 'Remote config fetch failed, using env var defaults',
				level: 'warning' as Sentry.Severity,
				data: { error: error instanceof Error ? error.message : String(error) },
			});

			const defaultConfig = getDefaultConfig();
			configCache = defaultConfig;
			return defaultConfig;
		} finally {
			initializationPromise = null;
		}
	})();

	return initializationPromise;
}

/**
 * Get current gateway config (synchronous).
 * Returns cached config if initialized, otherwise env var defaults.
 */
export function getGatewayConfig(): GatewayConfig {
	return configCache ?? getDefaultConfig();
}

/**
 * Check if gateway config has been initialized from remote
 */
export function isGatewayConfigInitialized(): boolean {
	return configCache !== null;
}

/**
 * Clear config cache (for testing)
 */
export function clearGatewayConfigCache(): void {
	configCache = null;
	initializationPromise = null;
}
